import { Box } from '@doda/io-react/common';
import { Favorite } from 'components/Favorite';
import { JobDetails } from 'components/RecommendCard/components/JobDetails';
import { JobName } from 'components/RecommendCard/components/JobName';
import React, { ComponentProps, FC } from 'react';
import classnames from 'classnames';
import { omitCompanyName } from 'components/RecommendCard/logics/omitCompanyName';
import styles from './index.module.scss';

type FavoriteProps = ComponentProps<typeof Favorite>;

/**
 * レコメンドカード
 * @param {string} companyName 企業名
 * @param {string} jobName 職種
 * @param {string} area 勤務地
 * @param {string} income 給与
 * @param {string} jobDetailUrl 求人データ
 * @param {boolean} isJobDetails [職種][給与]の表示有無
 * @param {string} bookmarkListUrl 気になるリストのURL
 * @param {boolean} isNew [NEW]タグ表示有無
 * @param {boolean} isDeadline [締切間近]タグ表示有無
 * @param {Void} onClickFavorite [気になる]ボタン押下時に発火
 * @param {Void} onClickSaved [気になるリストへ]ボタン押下時に発火
 * @param {Void} onClickCardDetail [会社名][職種]部分押下時に発火
 * @param {string} jid 求人ID
 * @param {object} saveBookmarkRequestBody 気になる登録APIに必要なパラメータ
 * @param {string} shadowColor 影の色 背景が青の時はblue、白・灰色の時はblackを指定
 * @param {boolean} isBookmarked 気になる済みかどうか
 */
interface Props {
  companyName: string | undefined;
  jobName: string | undefined;
  area: string | undefined;
  income: string | undefined;
  jobDetailUrl: string;
  isJobDetails?: boolean;
  bookmarkListUrl: FavoriteProps['bookmarkListUrl'];
  onClickFavorite: FavoriteProps['onClickFavorite'];
  onClickSaved: FavoriteProps['onClickSaved'];
  onClickCardDetail: () => void;
  isNew?: boolean;
  isDeadline?: boolean;
  jid: FavoriteProps['jid'] | undefined;
  saveBookmarkRequestBody: FavoriteProps['requestBody'];
  shadowColor?: 'blue' | 'black';
  isBookmarked?: FavoriteProps['isBookmarked'];
}

export const JobRecommendCard: FC<Props> = ({
  companyName,
  jobName,
  area,
  income,
  jobDetailUrl,
  isJobDetails = true,
  bookmarkListUrl,
  isNew,
  isDeadline,
  onClickFavorite,
  onClickSaved,
  onClickCardDetail,
  jid,
  saveBookmarkRequestBody,
  shadowColor = 'blue',
  isBookmarked = false,
}) => {
  if (!companyName || !jobName || !area || !income || !jid) {
    // JobSearchResultResponseにてundefinedで返ってくる恐れがあるためリターンする
    return <></>;
  }
  return (
    <Box
      color="white"
      boxShadow={shadowColor}
      radius="4"
      className={styles.recommendCard}
    >
      <a
        href={jobDetailUrl}
        onClick={onClickCardDetail}
        className={classnames(styles.cardDetail__link, {
          [styles['jobDetails--invisible']]: isJobDetails === false,
        })}
        target="_blank"
        rel="noopener"
      >
        <Box
          className={classnames(styles.recommendCard__cardDetail, {
            [styles['recommendCard--isJobDetails']]: isJobDetails,
            [styles['recommendCard--notJobDetails']]: !isJobDetails,
          })}
          radius="4"
        >
          <h4 className={styles.recommendCard__companyName}>
            {omitCompanyName(companyName, isNew, isDeadline)}
          </h4>
          <JobName jobName={jobName} />
          {isJobDetails && <JobDetails area={area} income={income} />}
        </Box>
      </a>

      <Favorite
        jid={jid}
        requestBody={saveBookmarkRequestBody}
        size="S"
        width="100%"
        fadeTrigger="timer"
        isBookmarked={isBookmarked}
        bookmarkListUrl={bookmarkListUrl}
        onClickFavorite={onClickFavorite}
        onClickSaved={onClickSaved}
      />
    </Box>
  );
};
